<template>
  <div class="flex flex-col gap-4">
    <p class="text-[20px] font-helvetica-700 font-bold text-gray-dark">
      Сохраненные адреса доставки
    </p>
    <div class="flex flex-col gap-3 lg:hidden">
      <template v-if="loading">
        <div
          v-for="index in 3"
          :key="`settings-address-mobile-loading-${index}`"
          class="rounded-[18px] bg-white-gray p-4"
        >
          <div class="shimmer-row h-5 w-full rounded-[8px]"></div>
        </div>
      </template>

      <article
        v-else-if="tableRows.length"
        v-for="row in tableRows"
        :key="row.id"
        class="rounded-[18px] border border-gray-border bg-white p-4"
      >
        <dl class="divide-y divide-gray-border text-[14px] leading-[20px] text-gray-dark">
          <div class="grid gap-1 pb-2.5">
            <dt class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Адрес</dt>
            <dd class="font-helvetica-700">{{ row.address }}</dd>
          </div>
          <div class="grid gap-3 py-2.5">
            <div class="grid grid-cols-[112px_minmax(0,1fr)] gap-3">
              <dt class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Контакт</dt>
              <dd>{{ row.contact || "—" }}</dd>
            </div>
            <div class="grid grid-cols-[112px_minmax(0,1fr)] gap-3">
              <dt class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Телефоны</dt>
              <dd>{{ row.phones || "—" }}</dd>
            </div>
          </div>
          <div class="grid gap-3 pt-2.5">
            <div class="grid grid-cols-[112px_minmax(0,1fr)] gap-3">
              <dt class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Нас. пункт</dt>
              <dd>{{ row.locality || "—" }}</dd>
            </div>
            <div class="grid grid-cols-[112px_minmax(0,1fr)] gap-3">
              <dt class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Регион</dt>
              <dd>{{ row.region || "—" }}</dd>
            </div>
          </div>
        </dl>
      </article>

      <div
        v-else
        class="rounded-[18px] border border-gray-border bg-white px-4 py-8 text-center text-[15px] text-extra-text-color"
      >
        Адреса доставки пока не добавлены
      </div>
    </div>

    <div
      class="hidden overflow-x-auto rounded-[20px] border border-gray-border bg-white shadow-panel-soft lg:block"
    >
      <table class="min-w-[820px] w-full table-fixed border-collapse text-left">
        <colgroup>
          <col class="w-[22%]" />
          <col class="w-[18%]" />
          <col class="w-[20%]" />
          <col class="w-[20%]" />
          <col class="w-[20%]" />
        </colgroup>
        <thead
          class="border-b border-gray-border text-[16px] font-helvetica-700 font-bold text-gray-dark"
        >
          <tr>
            <th class="py-4 pl-5 pr-3 font-bold whitespace-nowrap">Адрес</th>
            <th class="py-4 px-3 font-bold whitespace-nowrap">
              Контактное лицо
            </th>
            <th class="py-4 px-3 font-bold whitespace-nowrap">Телефоны</th>
            <th class="py-4 px-3 font-bold whitespace-nowrap">Нас. пункт</th>
            <th class="py-4 px-5 font-bold whitespace-nowrap">Регион</th>
          </tr>
        </thead>
        <tbody
          class="divide-y divide-gray-border text-[16px] font-helvetica-400 font-normal text-gray-dark"
        >
          <tr v-if="loading">
            <td colspan="5" class="py-4 px-3">
              <div class="shimmer-row h-5 w-full rounded-[8px]"></div>
            </td>
          </tr>
          <tr v-else-if="!tableRows.length">
            <td
              colspan="5"
              class="px-5 py-8 text-center text-[16px] text-extra-text-color"
            >
              Адреса доставки пока не добавлены
            </td>
          </tr>
          <tr
            v-else
            v-for="row in tableRows"
            :key="row.id"
            class="transition-colors hover:bg-white-gray"
          >
            <td class="py-3 pl-5 pr-3 align-middle">
              <div class="flex min-h-[45px] items-center px-5">
                {{ row.address }}
              </div>
            </td>
            <td class="py-3 px-3 align-middle">
              <div class="flex min-h-[45px] items-center px-5">
                {{ row.contact }}
              </div>
            </td>
            <td class="py-3 px-3 align-middle">
              <div class="flex min-h-[45px] items-center px-5">
                {{ row.phones }}
              </div>
            </td>
            <td class="py-3 px-3 align-middle">
              <div class="flex min-h-[45px] items-center px-5">
                {{ row.locality }}
              </div>
            </td>
            <td class="py-3 px-5 align-middle">
              <div class="flex min-h-[45px] items-center px-5">
                {{ row.region }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

type AddressRow = {
  id: string;
  address: string;
  contact: string;
  phones: string;
  locality: string;
  region: string;
};

const props = defineProps<{
  rows?: AddressRow[];
  loading?: boolean;
}>();

const tableRows = computed(() => props.rows ?? []);
const loading = computed(() => props.loading ?? false);
</script>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer-row {
  background: var(--gradient-shimmer);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}
</style>
